import { useState, useEffect } from 'react';
import { Loader2, CheckCircle, XCircle, Download, ExternalLink } from 'lucide-react';
import { getVideoStatus } from '@/services/agents/vgenAgentService';

interface VGenVideoStatusProps {
  videoId: string;
  isDarkMode: boolean;
}

export const VGenVideoStatus = ({ videoId, isDarkMode }: VGenVideoStatusProps) => {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const checkStatus = async () => {
      try {
        const data = await getVideoStatus(videoId);
        setStatus(data);
        setError(null);

        // Stop polling if completed or failed
        if (data?.status === 'completed' || data?.status === 'failed') {
          if (intervalId) clearInterval(intervalId);
        }
      } catch (err: any) {
        console.error(`[VGen] Status check failed:`, err);
        const errorMsg = err?.message || 'Failed to fetch video status';
        setError(errorMsg);
        // Don't stop polling on network errors, might be temporary
        if (errorMsg.includes('CORS') || errorMsg.includes('proxy')) {
          console.warn('[VGen] CORS/Proxy error - ensure proxy server is running on port 3001');
        }
      } finally {
        setLoading(false);
      }
    };

    // Initial check
    checkStatus();

    // Poll every 5 seconds if not completed/failed
    intervalId = setInterval(() => {
      if (status?.status !== 'completed' && status?.status !== 'failed') {
        checkStatus();
      }
    }, 5000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [videoId]);

  const getStatusColor = () => {
    switch (status?.status) {
      case 'completed':
        return 'text-green-500';
      case 'failed':
        return 'text-red-500';
      case 'processing':
        return 'text-blue-500';
      case 'pending':
      case 'waiting':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const getStatusIcon = () => {
    switch (status?.status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Loader2 className="w-5 h-5 animate-spin text-blue-500" />;
    }
  };

  if (loading && !status) {
    return (
      <div className={`p-4 rounded-xl border ${
        isDarkMode ? 'bg-[#2e2e2e] border-gray-700' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span>Checking video status...</span>
        </div>
      </div>
    );
  }

  if (error) {
    const isCorsError = error.includes('CORS') || error.includes('Failed to fetch') || error.includes('ERR_FAILED');
    return (
      <div className={`p-4 rounded-xl border ${
        isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'
      }`}>
        <div className="space-y-3">
          <div className="flex items-center gap-3 text-red-500">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
          {isCorsError && (
            <div className={`text-sm p-3 rounded-lg ${
              isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
            }`}>
              <div className="font-medium mb-1">⚠️ Proxy Server Required</div>
              <div className="space-y-1 text-xs">
                <div>1. Open terminal in project root</div>
                <div>2. Run: <code className="bg-black/20 px-1 py-0.5 rounded">node proxy-server.js</code></div>
                <div>3. Ensure it shows "running on port 3001"</div>
                <div>4. Refresh this page</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isDarkMode ? 'bg-[#2e2e2e] border-gray-700' : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getStatusIcon()}
            <div>
              <div className="font-semibold">Video Generation</div>
              <div className="text-sm opacity-90">ID: {videoId.slice(0, 16)}...</div>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor()} bg-white/20`}>
            {status?.status?.toUpperCase() || 'UNKNOWN'}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Progress Bar for processing */}
        {(status?.status === 'processing' || status?.status === 'pending' || status?.status === 'waiting') && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="opacity-70">Processing your video...</span>
              <span className="font-medium">
                {status?.status === 'processing' ? '⚙️ Rendering' : '⏳ In Queue'}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse" 
                   style={{ width: status?.status === 'processing' ? '75%' : '25%' }} />
            </div>
          </div>
        )}

        {/* Video Details */}
        {status?.duration && (
          <div className="flex items-center gap-2 text-sm">
            <span className="opacity-70">Duration:</span>
            <span className="font-medium">{status.duration}s</span>
          </div>
        )}

        {status?.created_at && (
          <div className="flex items-center gap-2 text-sm">
            <span className="opacity-70">Created:</span>
            <span className="font-medium">{new Date(status.created_at).toLocaleString()}</span>
          </div>
        )}

        {/* Video Player for completed */}
        {status?.status === 'completed' && status?.video_url && (
          <div className="space-y-3">
            <video
              controls
              className="w-full rounded-lg"
              src={status.video_url}
              poster={status.thumbnail_url}
            >
              Your browser does not support the video tag.
            </video>
            
            <div className="flex gap-2">
              <a
                href={status.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDarkMode 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                <ExternalLink className="w-4 h-4" />
                Open Video
              </a>
              {status.caption_url && (
                <a
                  href={status.caption_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  <Download className="w-4 h-4" />
                  Captions
                </a>
              )}
            </div>

            <div className={`text-xs p-2 rounded ${
              isDarkMode ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
            }`}>
              ⚠️ Note: Video URLs expire in ~7 days. Download if you need to keep it.
            </div>
          </div>
        )}

        {/* Error Message */}
        {status?.status === 'failed' && (
          <div className={`p-3 rounded-lg ${
            isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-700'
          }`}>
            <div className="font-medium mb-1">Generation Failed</div>
            <div className="text-sm">{status?.error || 'Unknown error occurred'}</div>
          </div>
        )}
      </div>
    </div>
  );
};
